import React, { useState, useEffect, useCallback } from 'react';
import { groupAPI } from '../services/api';
import { getDiceBearGroupAvatar } from '../services/dicebear';
import { getDiceBearUserAvatar } from '../services/dicebear';
import CreateStack from './CreateStack';
import EditableTextArea from './EditableTextArea';


const GroupProfile = ({ groupId, onBack, onClose }) => {
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [showNotFoundDialog, setShowNotFoundDialog] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [showLeaveConfirmDialog, setShowLeaveConfirmDialog] = useState(false);
  const [showRetireConfirmDialog, setShowRetireConfirmDialog] = useState(false);
  const [createStackDialog, setCreateStackDialog] = useState({ isOpen: false, mode: null, targetId: null, targetName: null, targetAvatar: null });
  const [bots, setBots] = useState([]);
  const [updateDescriptionLoading, setUpdateDescriptionLoading] = useState(false);

  const loadGroupDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await groupAPI.getGroup(groupId);
      setGroup(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setShowNotFoundDialog(true);
      } else {
        console.error('Failed to load group details:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  const loadBots = useCallback(async () => {
    try {
      const response = await groupAPI.getGroupBots(groupId);
      setBots(response.data);
    } catch (err) {
      console.error('Failed to load group bots:', err);
      setBots([]);
    }
  }, [groupId]);

  useEffect(() => {
    loadGroupDetails();
    loadBots();
  }, [loadGroupDetails, loadBots]);

  const handleInviteFriend = async () => {
    if (!selectedFriend) return;
    
    try {
      setInviting(true);
      await groupAPI.inviteUserToGroup(groupId, selectedFriend.id);
      setShowInviteDialog(false);
      setSelectedFriend(null);
      await loadGroupDetails(); // Refresh to update non-members list
      
      // Trigger notification refresh for the invited user
      window.dispatchEvent(new Event('notificationRefresh'));
    } catch (err) {
      if (err.response?.status === 404) {
        setShowNotFoundDialog(true);
      } else {
        console.error('Failed to invite user to group:', err);
      }
    } finally {
      setInviting(false);
    }
  };

  const handleLeaveGroup = async () => {
    try {
      await groupAPI.leaveGroup(groupId);
      setShowLeaveConfirmDialog(false);
      onBack(); // Go back to groups list
    } catch (err) {
      if (err.response?.status === 404) {
        setShowNotFoundDialog(true);
      } else {
        console.error('Failed to leave group:', err);
      }
    }
  };

  const handleJoinGroup = async () => {
    try {
      await groupAPI.joinGroup(groupId);
      // Refresh the group details to show updated member status
      await loadGroupDetails();
    } catch (err) {
      if (err.response?.status === 404) {
        setShowNotFoundDialog(true);
      } else {
        console.error('Failed to join group:', err);
      }
    }
  };

  const handleRetireGroup = async () => {
    try {
      await groupAPI.retireGroup(groupId);
      setShowRetireConfirmDialog(false);
      onBack(); // Go back to groups list
    } catch (err) {
      if (err.response?.status === 404) {
        setShowNotFoundDialog(true);
      } else {
        console.error('Failed to retire group:', err);
      }
    }
  };

  const getGroupAvatarUrl = (groupName) => {
    return getDiceBearGroupAvatar(groupName);
  };


  const handleCreateStackSuccess = useCallback(async (stackData) => {
    // Refresh the stacks list or navigate to the new stack
    try {
      // You could refresh the stacks list here if needed
      console.log('Stack created successfully:', stackData);
    } catch (_) {}
  }, []);






  const handleRemoveUser = async () => {
    if (!memberToRemove) return;
    try {
      await groupAPI.blockUserFromGroup(groupId, memberToRemove.user_id);
      setShowRemoveConfirm(false);
      setMemberToRemove(null);
      await loadGroupDetails();
    } catch (err) {
      console.error('Failed to remove user from group:', err);
    }
  };

  const handleDescriptionSave = async (description) => {
    try {
      setUpdateDescriptionLoading(true);
      await groupAPI.updateGroupDescription(groupId, description);
      // Refresh group details to get updated description
      await loadGroupDetails();
    } catch (err) {
      console.error('Failed to update group description:', err);
      throw err; // Re-throw to let EditableTextArea handle the error
    } finally {
      setUpdateDescriptionLoading(false);
    }
  };

  const handleDescriptionRevert = () => {
    // No additional action needed, EditableTextArea handles the revert
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!group) {
    return <div>Group not found</div>;
  }

  // More robust member check with type conversion
  const isMember = group.members.some(member => 
    parseInt(member.user_id) === parseInt(group.current_user_id)
  );
  const isOwner = parseInt(group.owner_id) === parseInt(group.current_user_id);
  
  

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '30px' }}>
        <img 
          src={getGroupAvatarUrl(group.name)} 
          alt={`${group.name} avatar`}
          style={{ width: '100px', height: '100px', borderRadius: '50%' }}
        />
        <div>
          <h2>{group.name}</h2>
          <p><strong>Owner:</strong> {group.owner.username}</p>
          <p><strong>Members:</strong> {group.member_count}</p>
          <p><strong>Boards:</strong> {group.board_count ?? 0}</p>
          {!group.is_public && (
            <p style={{ color: 'var(--color-warning)', fontWeight: 'bold' }}>Private Group</p>
          )}
        </div>
      </div>

      {isOwner ? (
        <EditableTextArea
          value={group.description || ''}
          onSave={handleDescriptionSave}
          onRevert={handleDescriptionRevert}
          placeholder="Enter group description..."
          maxLength={64}
          label="Description"
          loading={updateDescriptionLoading}
        />
      ) : (
        group.description && (
          <div style={{ marginBottom: '20px', fontStyle: 'italic', fontWeight: 'bold', color: 'var(--color-text)' }}>
            {group.description}
          </div>
        )
      )}

      {isMember && (
        <>
          {/* Bots section */}
          {bots.length > 0 && (
            <div style={{ marginBottom: '30px' }}>
              <h3>Bots</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                {bots.map((bot, index) => (
                  <div 
                    key={`${bot.id}-${index}`} 
                    className="group-card" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      try { window.dispatchEvent(new CustomEvent('openBot', { detail: { botId: bot.id } })); } catch (_) {}
                    }}
                  >
                    <div style={{ flexShrink: 0 }}>
                      <img 
                        src={`/assets/${bot.avatar_id}`} 
                        alt={`${bot.name} avatar`}
                        style={{ width: '40px', height: '40px', borderRadius: '50%' }}
                      />
                    </div>
                    <div className="group-info" style={{ flex: 1 }}>
                      <div className="group-name">{bot.name}</div>
                      <div style={{ color: 'var(--color-text-muted)', fontSize: '12px', textTransform: 'lowercase' }}>
                        {bot.type.toLowerCase().replace('_', ' ')}
                      </div>
                      <div style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
                        Bot
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginBottom: '30px' }}>
            <h3>Members</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
              {group.members.map((member, index) => (
                <div key={`${member.id}-${index}`} className="group-card" style={{ cursor: 'default' }}>
                  <div style={{ flexShrink: 0 }}>
                    <img 
                      src={getDiceBearUserAvatar(member.user.username, member.user.avatar_id)} 
                      alt={`${member.user.username} avatar`}
                      style={{ width: '40px', height: '40px', borderRadius: '50%' }}
                    />
                  </div>
                  <div className="group-info" style={{ flex: 1 }}>
                    <div className="group-name">{member.user.username}</div>
                    <div style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
                      {member.role === 'OWNER' ? 'Owner' : 'Member'}
                    </div>
                  </div>
                  {isOwner && member.role !== 'OWNER' && (
                    <button
                      className="btn"
                      style={{ 
                        backgroundColor: 'var(--color-danger)', 
                        color: 'var(--text-on-danger)', 
                        fontSize: '12px',
                        padding: '6px 12px',
                        minWidth: 'auto',
                        flexShrink: 0
                      }}
                      onClick={() => { setMemberToRemove(member); setShowRemoveConfirm(true); }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Show Invite Friends section only if user is a member AND (group is not restricted OR user is the owner) */}
          {(!group.is_restricted || isOwner) && (
            <div style={{ marginBottom: '30px' }}>
              <h3>Invite Friends</h3>
              {group.non_members.length === 0 ? (
                <p>All your friends are already members of this group</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                  {group.non_members.map((friend, index) => {
                    const hasPendingInvitation = group.pending_invitation_user_ids && 
                      group.pending_invitation_user_ids.includes(friend.id);
                    
                    return (
                      <div key={`${friend.id}-${index}`} className="group-card" style={{ cursor: 'default' }}>
                        <div style={{ flexShrink: 0 }}>
                          <img 
                            src={getDiceBearUserAvatar(friend.username, friend.avatar_id)} 
                            alt={`${friend.username} avatar`}
                            style={{ width: '40px', height: '40px', borderRadius: '50%' }}
                          />
                        </div>
                        <div className="group-info" style={{ flex: 1 }}>
                          <div className="group-name">{friend.username}</div>
                          <div style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
                            {hasPendingInvitation ? 'Invitation sent' : 'Available to invite'}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            if (!hasPendingInvitation) {
                              setSelectedFriend(friend);
                              setShowInviteDialog(true);
                            }
                          }}
                          className="btn"
                          style={{ 
                            backgroundColor: hasPendingInvitation ? '#6c757d' : 'var(--color-success)', 
                            color: hasPendingInvitation ? '#fff' : 'var(--text-on-success)', 
                            fontSize: '12px',
                            padding: '6px 12px',
                            minWidth: 'auto',
                            cursor: hasPendingInvitation ? 'not-allowed' : 'pointer',
                            opacity: hasPendingInvitation ? 0.6 : 1,
                            flexShrink: 0
                          }}
                          disabled={hasPendingInvitation}
                        >
                          {hasPendingInvitation ? 'Invited' : 'Invite'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <div style={{ marginTop: '30px' }}>
        <h3>Group Actions</h3>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button onClick={onBack} className="btn" style={{ backgroundColor: 'var(--color-primary)', color: 'var(--text-on-primary)' }}>
            Back
          </button>
          {isMember && (
            <button 
              onClick={() => {
                setCreateStackDialog({
                  isOpen: true,
                  mode: 'group',
                  targetId: group.id,
                  targetName: group.name,
                  targetAvatar: getDiceBearGroupAvatar(group.name)
                });
              }}
              className="btn"
              style={{ backgroundColor: 'var(--color-success)', color: 'var(--text-on-success)' }}
            >
              Add Stack
            </button>
          )}
          
          {/* Show Join button only if user is not a member and group is public */}
          {!isMember && group.is_public && (
            <button 
              onClick={handleJoinGroup}
              className="btn"
              style={{ backgroundColor: 'var(--color-success)', color: 'var(--text-on-success)' }}
              data-tour="join-group"
            >
              Join Group
            </button>
          )}
          
          {/* Show Leave button only if user is a member but not the owner */}
          {isMember && !isOwner && (
            <button 
              onClick={() => setShowLeaveConfirmDialog(true)}
              className="btn"
              style={{ backgroundColor: 'var(--color-danger)', color: 'var(--text-on-danger)' }}
            >
              Leave Group
            </button>
          )}
          
          {/* Show Retire Group button if user is the owner */}
          {isOwner && (
            <button 
              onClick={() => setShowRetireConfirmDialog(true)}
              className="btn"
              style={{ 
                backgroundColor: group.members.length === 1 ? 'var(--color-danger)' : '#6c757d',
                opacity: group.members.length === 1 ? 1 : 0.6,
                cursor: group.members.length === 1 ? 'pointer' : 'not-allowed'
              }}
              disabled={group.members.length > 1}
            >
              Retire Group
            </button>
          )}
        </div>
      </div>

      {/* Show explanatory text for retire group button if user is the owner */}
      {isOwner && (
        <div style={{ 
          color: '#6c757d', 
          fontSize: '12px', 
          fontStyle: 'italic',
          marginTop: '10px',
          textAlign: 'center'
        }}>
          You can only retire a group if you are the only member
        </div>
      )}

      {/* Invite Confirmation Dialog */}
      {showInviteDialog && selectedFriend && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--overlay)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text)',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '400px',
            textAlign: 'center',
            boxShadow: '0 4px 6px var(--color-shadow)'
          }}>
            <h3>Invite user to group?</h3>
            <p>Send an invitation to <strong>{selectedFriend.username}</strong> to join <strong>{group.name}</strong>?</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
              <button 
                onClick={handleInviteFriend}
                className="btn"
                style={{ backgroundColor: 'var(--color-success)', color: 'var(--text-on-success)' }}
                disabled={inviting}
              >
                {inviting ? 'Inviting...' : 'Yes, Invite'}
              </button>
              <button 
                onClick={() => {
                  setShowInviteDialog(false);
                  setSelectedFriend(null);
                }}
                className="btn"
                style={{ backgroundColor: 'var(--color-border)', color: 'var(--color-text)' }}
                disabled={inviting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leave Group Confirmation Dialog */}
      {showLeaveConfirmDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--overlay)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text)',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '400px',
            textAlign: 'center',
            boxShadow: '0 4px 6px var(--color-shadow)'
          }}>
            <h3>Leave Group?</h3>
            <p>Are you sure you want to leave <strong>{group.name}</strong>?</p>
            <p style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
              You will no longer be able to see group content or invite friends.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
              <button 
                onClick={handleLeaveGroup}
                className="btn"
                style={{ backgroundColor: 'var(--color-danger)', color: 'var(--text-on-danger)' }}
              >
                Yes, Leave Group
              </button>
              <button 
                onClick={() => setShowLeaveConfirmDialog(false)}
                className="btn"
                style={{ backgroundColor: 'var(--color-border)', color: 'var(--color-text)' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Retire Group Confirmation Dialog */}
      {showRetireConfirmDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--overlay)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text)',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '400px',
            textAlign: 'center',
            boxShadow: '0 4px 6px var(--color-shadow)'
          }}>
            <h3 style={{ color: 'var(--color-danger)' }}>Retire Group?</h3>
            <p>Are you sure you want to retire <strong>{group.name}</strong>?</p>
            <p style={{ color: 'var(--color-danger)', fontSize: '14px', fontWeight: 'bold' }}>
              This action cannot be undone. The group will be permanently retired.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
              <button 
                onClick={handleRetireGroup}
                className="btn"
                style={{ backgroundColor: 'var(--color-danger)', color: 'var(--text-on-danger)' }}
              >
                Yes, Retire Group
              </button>
              <button 
                onClick={() => setShowRetireConfirmDialog(false)}
                className="btn"
                style={{ backgroundColor: 'var(--color-border)', color: 'var(--color-text)' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove User Confirmation Dialog */}
      {showRemoveConfirm && memberToRemove && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--overlay)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text)',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '420px',
            textAlign: 'center',
            boxShadow: '0 4px 6px var(--color-shadow)'
          }}>
            <h3 style={{ color: 'var(--color-danger)' }}>Remove user from group?</h3>
            <p>Removing <strong>{memberToRemove.user.username}</strong> will also hide this group from them and prevent future invites.</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
              <button 
                onClick={handleRemoveUser}
                className="btn"
                style={{ backgroundColor: 'var(--color-danger)', color: 'var(--text-on-danger)' }}
              >
                Yes, Remove
              </button>
              <button 
                onClick={() => { setShowRemoveConfirm(false); setMemberToRemove(null); }}
                className="btn"
                style={{ backgroundColor: 'var(--color-border)', color: 'var(--color-text)' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Group Not Found Dialog */}
      {showNotFoundDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--overlay)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text)',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '400px',
            textAlign: 'center',
            boxShadow: '0 4px 6px var(--color-shadow)'
          }}>
            <h3 style={{ color: 'var(--color-danger)' }}>Group Not Found</h3>
            <p>The group you're looking for doesn't exist or has been deleted.</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
              <button 
                onClick={() => {
                  setShowNotFoundDialog(false);
                  onBack();
                }}
                className="btn"
                style={{ backgroundColor: 'var(--color-border)', color: 'var(--color-text)' }}
              >
                Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Stack Dialog */}
      <CreateStack
        isOpen={createStackDialog.isOpen}
        onClose={() => setCreateStackDialog({ isOpen: false, mode: null, targetId: null, targetName: null, targetAvatar: null })}
        onSuccess={handleCreateStackSuccess}
        mode={createStackDialog.mode}
        targetId={createStackDialog.targetId}
        targetName={createStackDialog.targetName}
        targetAvatar={createStackDialog.targetAvatar}
      />

    </div>
  );
};

export default GroupProfile; 